/* ============================================================
 * <lp-terms-modal>
 *   Terms & Conditions popup used on affiliate landing pages.
 *   - Opens on click of any [data-ltm-target="<modal id>"] trigger.
 *   - Closes on: X button, overlay click, Esc key.
 *   - Locks page scroll while open.
 *   - Traps keyboard focus inside the dialog while open and
 *     restores focus to the trigger on close.
 * ============================================================ */

if (!customElements.get('lp-terms-modal')) {
  class LpTermsModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.lp-terms-modal__dialog');
      this.scrollEl = this.querySelector('.lp-terms-modal__scroll');
      this.triggers = document.querySelectorAll(`[data-ltm-target="${this.id}"]`);
      this.lastFocused = null;

      this.handleKeydown = this.handleKeydown.bind(this);

      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
    }

    bindEvents() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      });

      this.querySelectorAll('[data-ltm-close]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      });

      document.addEventListener('keydown', this.handleKeydown);
    }

    handleKeydown(event) {
      if (!this.classList.contains('is-open')) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') this.trapFocus(event);
    }

    getFocusableElements() {
      if (!this.dialog) return [];
      const selector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(this.dialog.querySelectorAll(selector)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      );
    }

    trapFocus(event) {
      const focusable = this.getFocusableElements();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    open(trigger) {
      this.lastFocused = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ltm-open');

      if (this.scrollEl) this.scrollEl.scrollTop = 0;

      const closeBtn = this.querySelector('.lp-terms-modal__close');
      window.requestAnimationFrame(() => {
        if (!closeBtn) return;
        try {
          closeBtn.focus({ preventScroll: true });
        } catch (err) {
          closeBtn.focus();
        }
      });

      this.dispatchEvent(new CustomEvent('lp-terms-modal:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ltm-open');

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        try {
          this.lastFocused.focus({ preventScroll: true });
        } catch (err) {
          this.lastFocused.focus();
        }
      }
      this.lastFocused = null;

      this.dispatchEvent(new CustomEvent('lp-terms-modal:close', { bubbles: true }));
    }
  }

  customElements.define('lp-terms-modal', LpTermsModal);
}
